import { css } from '@emotion/react'
import { Theme } from 'bold-ui'
import { booleanToSimNaoEnum, simNaoEnumRecord } from 'components/form'
import { ACOMPANHAMENTO_TERRITORIO_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { BeneficioDbEnum } from 'graphql/types.generated'
import { beneficioRecord } from 'types/enums'
import { calculateAge } from 'util/date/calculateAge'
import { applyMask, masks } from 'util/mask'
import { isObjectDeepEmpty } from 'util/object'
import { isCpfLike } from 'util/validation'

export const formatNomeCidadao = (cidadao: { nome: string; nomeSocial?: string }) =>
  (cidadao.nomeSocial ?? cidadao.nome).titleCase()

export const getCpfOrCnsTitle = (cidadao: { cpf?: string; cns?: string } | undefined): string => {
  if (cidadao?.cpf) return 'CPF '
  if (cidadao?.cns) return 'CNS '
  return ''
}

export const formatJoinRespostas = (respostas: string | string[] | null) => {
  if (!respostas) return null
  if (Array.isArray(respostas)) {
    if (respostas.length === 0) return null
    return (
      respostas
        .map((resposta) => `${resposta}`)
        .sort((a, b) => a.localeCompare(b))
        .join(', ') + '.'
    )
  }
  return `${respostas}.`
}

export const formatSimNaoJoinRespostas = (
  status: boolean | null | undefined,
  respostas: string | string[] | null | undefined
) => {
  if (status === null || status === undefined) {
    return undefined
  }

  const formattedRespostas = formatJoinRespostas(respostas)
  const formattedStatus = simNaoEnumRecord[booleanToSimNaoEnum(status)]

  return [formattedStatus, formattedRespostas].filter((item) => item && item !== '').join(': ')
}

export const formatCpfCnsResponsavel = (cpfCnsResponsavel: string | undefined): string | undefined => {
  let cpfCns: string | undefined = undefined
  const isCpf = cpfCnsResponsavel && isCpfLike(cpfCnsResponsavel)

  if (isCpf) {
    cpfCns = `CPF ${applyMask(cpfCnsResponsavel, masks.cpf)}`
  } else if (cpfCnsResponsavel) {
    cpfCns = `CNS ${applyMask(cpfCnsResponsavel, masks.cns)}`
  }

  return cpfCns || undefined
}

export const formatBeneficios = (beneficios: BeneficioDbEnum[] = []) =>
  beneficios?.length > 0
    ? beneficios
        .map((item) => beneficioRecord[item])
        .join(', ')
        .replace(/, ([^,]*)$/, ' e $1')
    : '-'

export const getBotaoRetornarCallbackParams = (pathname: string, search: string): string => {
  const currentParams = new URLSearchParams(search)
  const destinationParams = new URLSearchParams()

  const existingCallbackUrl = currentParams.get('callbackUrl')
  const existingCallbackText = currentParams.get('callbackText')

  if (existingCallbackUrl) {
    destinationParams.set('callbackUrl', existingCallbackUrl)

    if (existingCallbackText) {
      destinationParams.set('callbackText', existingCallbackText)
    }
  } else {
    const deveGerarCallback =
      pathname.startsWith('/lista-atendimento/') || pathname.startsWith(`${ACOMPANHAMENTO_TERRITORIO_PATH}/`)

    if (deveGerarCallback) {
      const returnUrl = pathname + search
      destinationParams.set('callbackUrl', returnUrl)

      let textoBotaoRetorno = ''

      if (returnUrl.includes('/visualizarImovel/')) {
        textoBotaoRetorno = 'Retornar ao cadastro do imóvel'
      } else if (returnUrl.includes('/lista-atendimento/')) {
        textoBotaoRetorno = 'Retornar ao prontuário familiar'
      }

      if (textoBotaoRetorno) {
        destinationParams.set('callbackText', textoBotaoRetorno)
      }
    }
  }

  const searchString = destinationParams.toString()
  return searchString ? `?${searchString}` : ''
}

export const removeCidadaoIdParam = (search: string): string => {
  const params = new URLSearchParams(search)
  params.delete('cidadaoId')

  const nextSearch = params.toString()
  return nextSearch ? `?${nextSearch}` : ''
}

export const createFormSectionStyles = (theme: Theme) => ({
  rowsContainer: css`
    & > * {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.8rem 1rem;
    }
    & > *:not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.divider};
    }
  `,
  sectionContainer: css`
    & > * {
      padding: 0.8rem 0;
    }
  `,
  fixedWidthInputContainer: css`
    width: 25rem;
  `,
  borderBottom: css`
    border-bottom: 1px solid ${theme.pallete.divider};
  `,
  radioColumn: (minWidth: string) => ({
    vFlow: css`
      min-width: ${minWidth};
    `,
  }),
})

export const isFormEmpty = (form: any): boolean => {
  return form ? isObjectDeepEmpty((({ openedAccordion, ...rest }) => rest)(form)) : true
}

export const isCidadaoEntre12E21Anos = (dataNascimento: string): boolean => {
  if (!dataNascimento) return false

  const idade = calculateAge(dataNascimento).years
  return idade >= 12 && idade <= 21
}
